// Third-party
import React from 'react';

// Components
import { InfoTooltip, Tooltip } from '@/views/components/tooltips';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { CARBON_INTENSITY, CarbonIntensityLimit, ESGColourDefinition } from '@/enums/Esg';
import HoldingIcon from '@/views/components/common/HoldingIcon';
import { formatLocalizedString } from '@/utils/i18n/i18n';
import ComparisonItem from '@/types/comparison-item';
import ThemeMode from '@/types/theme-mode';
import classNames from 'classnames';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  selectedItems: Array<ComparisonItem>;
  theme?: ThemeMode;
};

const CarbonIntensity = ({ selectedItems, theme }: Props) => {
  const decimalsAllowed = 1;
  const maxValueToDisplay = 10000;

  const hasMultiple = selectedItems?.length > 1;

  const firstItem = selectedItems[0];
  const secondItem = selectedItems[1];

  const firstIntensity = firstItem?.esg?.carbonIntensity;
  const secondIntensity = secondItem?.esg?.carbonIntensity;

  const isSelectedItensity = (intensity?: number) => {
    if (intensity === undefined) return;

    if (intensity < CarbonIntensityLimit.VERY_LOW) {
      return CARBON_INTENSITY.VERY_LOW;
    }

    if (intensity < CarbonIntensityLimit.LOW) {
      return CARBON_INTENSITY.LOW;
    }

    if (intensity < CarbonIntensityLimit.MODERATE) {
      return CARBON_INTENSITY.MODERATE;
    }

    if (intensity < CarbonIntensityLimit.HIGH) {
      return CARBON_INTENSITY.HIGH;
    }

    return CARBON_INTENSITY.VERY_HIGH;
  };

  const renderDelta = () => {
    let icon;
    let color;
    let delta: number;
    if (firstIntensity && secondIntensity) {
      delta = +(firstIntensity - secondIntensity).toFixed(decimalsAllowed);
    } else {
      return;
    }
    switch (true) {
      case delta < 0:
        icon = 'icon-arrow-up';
        color = ESGColourDefinition.VERY_HIGH;
        break;
      case delta > 0:
        icon = 'icon-arrow-down';
        color = ESGColourDefinition.VERY_LOW;
        break;
      default:
        icon = 'icon-minus-med';
        color = ESGColourDefinition.MODERATE;
        break;
    }
    return (
      <div className="delta" style={{ color }}>
        <i className={`${icon} me-1`} />
        <FormattedMessage id="ESGComparisonTool.Carbon.IntensityUnitShort" values={{ intensity: Math.abs(delta) }} />
      </div>
    );
  };

  const renderScaleCategories = () =>
    Object.values(CARBON_INTENSITY).map((item: string, index: number) => (
      <li
        key={`intensity-${index}`}
        className={classNames({
          active: item === isSelectedItensity(firstIntensity),
          'active-second': item === isSelectedItensity(secondIntensity),
          'is-low-or-high': item === CARBON_INTENSITY.HIGH || item === CARBON_INTENSITY.LOW,
        })}
      >
        <FormattedMessage id={`ESGComparisonTool.Carbon.Intensity.${item}`} />
        <h3 className={classNames('d-flex align-flex-end', { 'not-applicable': !hasMultiple && !firstIntensity })}>
          {!hasMultiple && !firstIntensity && formatMessage('Common.NotApplicable')}
          {firstIntensity && firstItem && item === isSelectedItensity(firstIntensity) && (
            <>
              <span className="intensity-container">
                {hasMultiple && (
                  <Tooltip message={formatLocalizedString(firstItem.name)}>
                    <span>
                      <HoldingIcon type={firstItem.type} color={firstItem.color} />
                    </span>
                  </Tooltip>
                )}
                {!isSelectedItensity(firstIntensity) || firstItem === undefined ? (
                  formatMessage('Common.NotApplicable')
                ) : (
                  <Tooltip effect="solid" disabled={firstIntensity < maxValueToDisplay} message={firstIntensity.toFixed(decimalsAllowed)}>
                    <span className="value">{firstIntensity.toFixed(decimalsAllowed)}</span>
                  </Tooltip>
                )}
                {isSelectedItensity(firstIntensity) && <FormattedMessage id="ESGComparisonTool.Carbon.IntensityUnit" />}
              </span>
              <span className="arrow" />
            </>
          )}
        </h3>
        {secondIntensity && secondItem && (
          <h3 className="d-block second-item">
            {!secondIntensity && formatMessage('Common.NotApplicable')}
            {item === isSelectedItensity(secondIntensity) && (
              <>
                <span className="arrow" />
                <span className="intensity-container">
                  {secondItem && (
                    <Tooltip message={formatLocalizedString(secondItem.name)}>
                      <span>
                        <HoldingIcon type={secondItem.type} color={secondItem.color} />
                      </span>
                    </Tooltip>
                  )}
                  {!isSelectedItensity(secondIntensity) || !secondItem ? (
                    formatMessage('Common.NotApplicable')
                  ) : (
                    <Tooltip effect="solid" disabled={secondIntensity < maxValueToDisplay} message={secondIntensity.toFixed(decimalsAllowed)}>
                      <span className="value">{secondIntensity.toFixed(decimalsAllowed)}</span>
                    </Tooltip>
                  )}
                  {isSelectedItensity(secondIntensity) && <FormattedMessage id="ESGComparisonTool.Carbon.IntensityUnit" />}
                </span>
                {renderDelta()}
              </>
            )}
          </h3>
        )}
      </li>
    ));

  return (
    <div className="carbon-intensity">
      <div className="d-flex align-items-center title">
        <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="header2Xs">
          {formatMessage('ESGComparisonTool.Carbon.Title')}
        </Typography>
        <FormattedMessage id="ESGComparisonTool.Carbon.TitleSubText" />
        {hasMultiple && (
          <InfoTooltip message={formatMessage('ESGComparisonTool.Carbon.EducationalText')} data-private data-dd-privacy="mask" className="ms-1" />
        )}
      </div>
      {hasMultiple && firstItem && !isSelectedItensity(firstIntensity) && (
        <div className="not-applicable first-item">
          <Tooltip message={formatLocalizedString(firstItem.name)}>
            <span>
              <HoldingIcon type={firstItem.type} color={firstItem.color} />
            </span>
          </Tooltip>
          <span className="font-weight-bold">{formatMessage('Common.NotApplicable')}</span>
        </div>
      )}

      <div className="intensity-scale">
        <div className="intensity-scale-bar" />
        <ul className={hasMultiple ? 'has-multiple' : ''}>{renderScaleCategories()}</ul>
      </div>

      {secondItem && !isSelectedItensity(secondIntensity) && (
        <div className="not-applicable second-item">
          <Tooltip message={formatLocalizedString(secondItem.name)}>
            <span>
              <HoldingIcon type={secondItem.type} color={secondItem.color} />
            </span>
          </Tooltip>
          <span className="font-weight-bold">{formatMessage('Common.NotApplicable')}</span>
        </div>
      )}
      {!hasMultiple && (
        <p className="educational-text">
          <FormattedMessage id="ESGComparisonTool.Carbon.EducationalText" />
        </p>
      )}
    </div>
  );
};

export default CarbonIntensity;
